/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  CreateCustomerAddressResponse,
  CreateCustomerAddressResult,
} from '@/account/types';
import { fetchGraphQl } from '../fetch-graphql';
import { CREATE_CUSTOMER_ADDRESS } from './graphql/createCustomerAddress.graphql';
import { handleNetworkError } from '@/account/lib/network-error';
import { handleFetchError } from '@/account/lib/fetch-error';
import { convertKeysCase } from '@/account/lib/convertCase';
import { CustomerAddressesModel } from '@/account/data/models';

export const createCustomerAddress = async (
  address: CustomerAddressesModel
): Promise<CreateCustomerAddressResult> => {
  return await fetchGraphQl(CREATE_CUSTOMER_ADDRESS, {
    method: 'POST',
    variables: {
      input: convertKeysCase(address, 'snakeCase', {
        custom_attributesV2: 'custom_attributesV2',
        firstName: 'firstname',
        lastName: 'lastname',
        middleName: 'middlename',
      }),
    },
  })
    .then((response: CreateCustomerAddressResponse) => {
      if (response.errors?.length) return handleFetchError(response.errors);

      const created = response?.data?.createCustomerAddress;
      return {
        firstname: created?.firstname ?? '',
        uid: created?.uid ?? '',
      };
    })
    .catch(handleNetworkError);
};
